/**
 * GridToolbar - Reusable toolbar for AG Grid components
 *
 * Features:
 * - Search/quick filter
 * - Column visibility toggle with checkboxes
 * - Filter dropdown
 * - Persistent column settings via localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import { Input, Dropdown, Button, Checkbox, Space, Divider, Popover, Select, DatePicker } from 'antd';
import './blueprint-grid.css';
import {
  SearchOutlined,
  SettingOutlined,
  FilterOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { GridApi } from 'ag-grid-community';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

export interface ColumnConfig {
  field: string; // field or colId - used as unique identifier
  headerName: string;
  visible?: boolean;
  alwaysVisible?: boolean; // Cannot be hidden (e.g., Name column)
}

export interface FilterConfig {
  field: string;
  label: string;
  type: 'select' | 'date' | 'dateRange';
  options?: { value: string; label: string }[];
}

export interface ActiveFilter {
  field: string;
  value: string | [string, string] | null;
}

interface GridToolbarProps {
  gridApi?: GridApi | null;
  columns: ColumnConfig[];
  filters?: FilterConfig[];
  storageKey: string; // For localStorage persistence
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  onColumnVisibilityChange?: (visibleColumns: string[]) => void;
  onFilterChange?: (filters: ActiveFilter[]) => void;
  quickFilterText?: string;
  extraButtons?: React.ReactNode;
}

// Load saved column visibility from localStorage
const loadColumnVisibility = (storageKey: string, defaultColumns: ColumnConfig[]): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem(`grid-columns-${storageKey}`);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parse errors
  }
  // Default: all columns visible
  return defaultColumns.reduce((acc, col) => {
    acc[col.field] = col.visible !== false;
    return acc;
  }, {} as Record<string, boolean>);
};

// Save column visibility to localStorage
const saveColumnVisibility = (storageKey: string, visibility: Record<string, boolean>) => {
  try {
    localStorage.setItem(`grid-columns-${storageKey}`, JSON.stringify(visibility));
  } catch {
    // Ignore storage errors
  }
};

export default function GridToolbar({
  gridApi,
  columns,
  filters = [],
  storageKey,
  searchPlaceholder = 'Search...',
  onSearchChange,
  onColumnVisibilityChange,
  onFilterChange,
  quickFilterText = '',
  extraButtons,
}: GridToolbarProps) {
  const [searchValue, setSearchValue] = useState(quickFilterText);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility(storageKey, columns)
  );
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Apply column visibility to grid when it changes
  useEffect(() => {
    if (gridApi) {
      columns.forEach(col => {
        const visible = col.alwaysVisible || columnVisibility[col.field] !== false;
        gridApi.setColumnsVisible([col.field], visible);
      });
    }
  }, [gridApi, columnVisibility, columns]);

  // Notify parent of visibility changes
  useEffect(() => {
    const visibleCols = columns
      .filter(col => col.alwaysVisible || columnVisibility[col.field] !== false)
      .map(col => col.field);
    onColumnVisibilityChange?.(visibleCols);
  }, [columnVisibility, columns, onColumnVisibilityChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearchChange?.(value);
  }, [onSearchChange]);

  const handleColumnToggle = useCallback((field: string) => (e: CheckboxChangeEvent) => {
    const newVisibility = {
      ...columnVisibility,
      [field]: e.target.checked,
    };
    setColumnVisibility(newVisibility);
    saveColumnVisibility(storageKey, newVisibility);
  }, [columnVisibility, storageKey]);

  const handleShowAllColumns = useCallback(() => {
    const newVisibility = columns.reduce((acc, col) => {
      acc[col.field] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setColumnVisibility(newVisibility);
    saveColumnVisibility(storageKey, newVisibility);
  }, [columns, storageKey]);

  const handleFilterChange = useCallback((field: string, value: string | [string, string] | null) => {
    setActiveFilters(prev => {
      const existing = prev.filter(f => f.field !== field);
      if (value) {
        return [...existing, { field, value }];
      }
      return existing;
    });
  }, []);

  // Notify parent of filter changes
  useEffect(() => {
    onFilterChange?.(activeFilters);
  }, [activeFilters, onFilterChange]);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    onFilterChange?.([]);
  }, [onFilterChange]);

  const removeFilter = useCallback((field: string) => {
    setActiveFilters(prev => prev.filter(f => f.field !== field));
  }, []);

  // Column visibility dropdown content
  const columnMenuContent = (
    <div style={{ padding: 8, minWidth: 180 }}>
      <div className="bp-toolbar-label">
        Show Columns
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {columns.map(col => (
          <div key={col.field} style={{ padding: '4px 0' }}>
            <Checkbox
              checked={col.alwaysVisible || columnVisibility[col.field] !== false}
              onChange={handleColumnToggle(col.field)}
              disabled={col.alwaysVisible}
            >
              <span style={{ fontSize: 13 }}>{col.headerName}</span>
            </Checkbox>
          </div>
        ))}
      </div>
      <Divider style={{ margin: '8px 0' }} />
      <Button type="link" size="small" onClick={handleShowAllColumns} style={{ padding: 0 }}>
        Show all columns
      </Button>
    </div>
  );

  // Filter popover content
  const filterContent = filters.length > 0 ? (
    <div style={{ padding: 8, minWidth: 220 }}>
      <div className="bp-toolbar-label">
        Filters
      </div>
      {filters.map(filter => (
        <div key={filter.field} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{filter.label}</div>
          {filter.type === 'select' && filter.options && (
            <Select
              style={{ width: '100%' }}
              placeholder={`Select ${filter.label.toLowerCase()}`}
              allowClear
              value={activeFilters.find(f => f.field === filter.field)?.value as string || undefined}
              onChange={(value) => handleFilterChange(filter.field, value)}
              options={filter.options}
              size="small"
            />
          )}
          {filter.type === 'date' && (
            <DatePicker
              style={{ width: '100%' }}
              size="small"
              onChange={(date) => handleFilterChange(filter.field, date?.toISOString() || null)}
            />
          )}
          {filter.type === 'dateRange' && (
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              size="small"
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  handleFilterChange(filter.field, [dates[0].toISOString(), dates[1].toISOString()]);
                } else {
                  handleFilterChange(filter.field, null);
                }
              }}
            />
          )}
        </div>
      ))}
      {activeFilters.length > 0 && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Button type="link" size="small" onClick={clearAllFilters} style={{ padding: 0 }}>
            Clear all filters
          </Button>
        </>
      )}
    </div>
  ) : null;

  // Get filter label for active filter badges
  const getFilterLabel = (filter: ActiveFilter) => {
    const config = filters.find(f => f.field === filter.field);
    if (!config) return filter.field;
    if (config.type === 'select' && config.options) {
      const option = config.options.find(o => o.value === filter.value);
      return `${config.label}: ${option?.label || filter.value}`;
    }
    if (config.type === 'date') {
      return `${config.label}: ${new Date(filter.value as string).toLocaleDateString()}`;
    }
    if (config.type === 'dateRange' && Array.isArray(filter.value)) {
      return `${config.label}: ${new Date(filter.value[0]).toLocaleDateString()} - ${new Date(filter.value[1]).toLocaleDateString()}`;
    }
    return `${config.label}: ${filter.value}`;
  };

  const visibleColumnCount = columns.filter(col => col.alwaysVisible || columnVisibility[col.field] !== false).length;

  return (
    <div style={{ marginBottom: 12 }}>
      <Space wrap>
        {/* Search */}
        <Input
          placeholder={searchPlaceholder}
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          value={searchValue}
          onChange={handleSearchChange}
          style={{ width: 240 }}
          allowClear
        />

        {/* Column visibility */}
        <Dropdown
          popupRender={() => columnMenuContent}
          trigger={['click']}
          placement="bottomLeft"
        >
          <Button icon={<SettingOutlined />}>
            Columns {visibleColumnCount < columns.length && `(${visibleColumnCount}/${columns.length})`}
          </Button>
        </Dropdown>

        {/* Filter button */}
        {filters.length > 0 && (
          <Popover
            content={filterContent}
            trigger="click"
            placement="bottomLeft"
            open={filterPopoverOpen}
            onOpenChange={setFilterPopoverOpen}
          >
            <Button
              icon={<FilterOutlined />}
              type={activeFilters.length > 0 ? 'primary' : 'default'}
            >
              Filter {activeFilters.length > 0 && `(${activeFilters.length})`}
            </Button>
          </Popover>
        )}

        {/* Extra buttons */}
        {extraButtons}
      </Space>

      {/* Active filter badges */}
      {activeFilters.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {activeFilters.map(filter => (
            <span key={filter.field} className="bp-filter-badge">
              {getFilterLabel(filter)}
              <CloseOutlined
                style={{ marginLeft: 6, cursor: 'pointer', fontSize: 10 }}
                onClick={() => removeFilter(filter.field)}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
